<script setup lang="ts">
  import { createEmptyAddress, type Address } from '@/types/nest/customer'
  import CustomerAddressForm from '@/components/customer/CustomerAddressForm.vue'
  import { deepEqual, Modal, UiButton, useToastStore } from '@logistiek-zonder-papier/lzp-library'
  import { ref, watch } from 'vue'
  import { useAddressStore } from '@/store/address.store'
  import { useAddressValidation } from '@/composables/validation/use-address-validation'

  const addressStore = useAddressStore()
  const toastStore = useToastStore()

  const address = ref<Address>(createEmptyAddress())
  const addressCopy = ref<Address>(createEmptyAddress())

  const { validate, visibleErrors, submitted } = useAddressValidation(address)

  const hasAddressChanges = ref(false)

  const show = defineModel<boolean>({ default: false, required: true })

  const { existingAddress = null, isEditable = false } = defineProps<{
    existingAddress?: Address | null
    isEditable?: boolean
  }>()

  const emit = defineEmits<{
    saved: [address: Address]
  }>()

  watch(
    show,
    isOpen => {
      if (!isOpen) return

      const base = existingAddress ? { ...existingAddress } : createEmptyAddress()

      address.value = { ...base }
      addressCopy.value = { ...base }
      hasAddressChanges.value = false
    },
    { immediate: true }
  )

  async function submit(close: () => void) {
    if (!validate()) return

    try {
      if (!existingAddress) {
        await addressStore.createAddress(address.value)
        await addressStore.fetchAddresses()
        toastStore.success('Adres succesvol aangemaakt')
      } else {
        await addressStore.updateCustomerAddress(address.value)
        toastStore.success('Adres succesvol bijgewerkt')
      }

      emit('saved', address.value)
      submitted.value = false
    } catch (error) {
      toastStore.error(
        !existingAddress
          ? `Adres kon niet aangemaakt worden: ${error}`
          : `Adres kon niet bijgewerkt worden: ${error}`
      )
      throw error
    }
    close()
  }

  watch(
    address,
    current => {
      if (!addressCopy.value) {
        hasAddressChanges.value = true
        return
      }

      if (!current) {
        hasAddressChanges.value = false
        return
      }

      const original = addressCopy.value

      hasAddressChanges.value = deepEqual(current, original)
    },
    { deep: true }
  )
</script>

<template>
  <Modal v-model:show="show">
    <CustomerAddressForm
      v-model="address"
      :saved="addressCopy"
      :isEditable="isEditable"
      @has-changed="hasAddressChanges = $event"
      :errors="visibleErrors"
    />

    <template #footer="{ close }">
      <div class="button-row">
        <UiButton label="Sluiten" @click="close" />
        <UiButton
          label="Opslaan"
          :disabled="!hasAddressChanges"
          @click="submit(close)"
          variant="prominent"
        />
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="css">
  .button-row {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
</style>
